import { personService } from '@hixme/api'
import {
  ROLE_BROKER,
  ROLE_CLIENT_ADMIN,
  ROLE_EMPLOYEE,
  ROLE_PLATFORM_HIXME_ADMIN,
} from '@hixme/role-policy'
import isArray from 'lodash/isArray'
import moment from 'moment'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import {
  change,
  getFormMeta,
  getFormValues,
  reduxForm,
  SubmissionError,
} from 'redux-form'
import { callWaiter, getWaiter } from 'redux-waiter'

// project
import { selectors as authSelectors } from '@hixme/auth-ui'
import { EmployeeForm, LoadingView } from 'components'
import modal from 'modules/app-core/modal'
import { actions as notification } from 'modules/app-core/notification-manager'
import { actions as BenefitsEffectiveDateActions } from 'modules/benefits-effective-dates'
import { POST_BENEFITEFFECTIVEDATES_ROUTE } from 'modules/benefits-effective-dates/constants'
import locations from 'modules/locations'
import { getRequestByName } from 'modules/redux-gateway'
import { actions as employeeBenefitsActions } from 'routes/Employees/Employee/CobraCoverage/modules/cobra-coverage'
import { actions as personActions } from 'store/modules/persons'
import { GET_ALL_PERSONS } from 'store/modules/persons/constants'
import PersonProvider from 'store/modules/persons/providers/PersonProvider'
import { personByIdSelector } from 'store/modules/persons/selectors'
import { selectors as userSessionSelectors } from 'store/modules/user-session'

// module
import { CustomActionNames, datadog } from 'helpers/datadog'
import useFeatureFlags from 'hooks/useFeatureFlags'
import withFeatureFlags from 'hooks/withFeatureFlags'
import { openActivityLogPopup } from 'modules/activity-log-service/activityLogPopupSlice'
import { ActivityLogAction } from 'modules/activity-log-service/constants'
import { postActivityLog } from 'modules/activity-log-service/helpers'
import { ActivityReasonCategory } from 'types/activity'
import { syncToNoyo } from 'enrollment-platform/api/partner-benefit-graph/client'
import { shouldSyncAfterRehireSave } from 'components/EmployeeForm/rehire-date'
import { updateRehireDate } from 'components/EmployeeForm/update-rehire-date'
import { toast } from 'react-toastify'
import {
  checkAddressWarning,
  sanitizeValues,
  validateAddress,
  validateEmployeeForm,
} from './utils'
import dayjs from 'dayjs'

const POST_ROUTE = 'personPost'
const FORM_NAME = 'addEmployee'

const mapStateToProps = (state, props) => {
  const isHixmeAdmin =
    [ROLE_PLATFORM_HIXME_ADMIN, ROLE_BROKER].indexOf(
      authSelectors.getRole(state)
    ) > -1
  const isPlatformAdmin = authSelectors.getRole(state) === ROLE_CLIENT_ADMIN
  const { flags } = useFeatureFlags()
  const isSalaryUnavailable = flags['admin.salary.unavailable']

  const company = userSessionSelectors.getCurrentCompany(state)

  // Redux form selectors
  const formValues = getFormValues(FORM_NAME)(state) || {}
  const metaValues = getFormMeta(FORM_NAME)(state) || {}

  const benefitsEffectiveDateMeta = metaValues.BenefitsEffectiveDate || {}
  const { action, person } = props

  // Show the warning checkbox only if we're editing a worker, the BED
  // is before 01/01/2018, and we've edited the BED field
  const showBEDCheckbox =
    action === 'edit' &&
    moment(formValues.BenefitsEffectiveDate).isBefore('2018-01-01', 'day') &&
    (benefitsEffectiveDateMeta.touched || false)

  const defaultValues = {
    BenefitsEligible: true,
    CompanyName: company.CompanyName,
    ExistingMedicalPlanWaived: true,
    IsExempt: false,
    IsTestUser: false,
    PayrollCycle: 0,
    ReimbursementEligible: true,
    Smoker: false,
    UserRole: ROLE_EMPLOYEE,
  }

  const editProps = () => {
    const prsn = personByIdSelector(state)(props.id)
    if (action === 'edit') {
      const editedPerson = prsn || {}
      editedPerson.OrganDonorSelection = editedPerson.OrganDonorOptInAt
        ? 'yes'
        : 'skip'
      return editedPerson
    }
    return {}
  }

  const initialValues = {
    ...defaultValues,
    ...editProps(),
  }

  const eligibleStatesForOrganDonorQuestion =
    flags['admin.eligible-states-for-organ-donor-question'] ?? []
  const stateProvince = resolveStateProvince(formValues, state)
  const isOrganDonorQuestionVisible = isStateEligibleForOrganDonorQuestion(
    stateProvince,
    eligibleStatesForOrganDonorQuestion
  )

  return {
    companyHasPlans: !!userSessionSelectors.currentCompanyHasPlans(state),
    companyId: company.Id,
    CompanyName:
      userSessionSelectors.getCurrentCompany(state).CompanyName || '',
    countyHash: locations.selectors.getCountyHashSelector(state) || [],
    fetchingBenefitEffectiveDates: getRequestByName(
      state,
      POST_BENEFITEFFECTIVEDATES_ROUTE
    ).isProcessing,
    formValues: getFormValues(FORM_NAME)(state),
    initialValues,
    isGetting: getWaiter(state, GET_ALL_PERSONS).isPending || false,
    isProcessing: getWaiter(state, POST_ROUTE).isPending || false,
    LoadingView,
    originalHireDate:
      person && moment(person.OriginalHireDate).format('M/D/YYYY'),
    personBED: formValues.BenefitsEffectiveDate,
    showBEDCheckbox,
    isHixmeAdmin,
    isPlatformAdmin,
    isSalaryUnavailable,
    isOrganDonorQuestionVisible,
  }
}

const resolveStateProvince = (form, state) => {
  // Start with state from form
  let stateProvince = form?.StateProvince

  // Override with state from county lookup if postal code and county are provided
  const stateFromCounty = getStateProvinceFromCounty(
    form?.PostalCode,
    form?.County,
    state
  )
  if (stateFromCounty) {
    stateProvince = stateFromCounty
  }

  return stateProvince
}

/**
 * Gets state/province from county lookup based on postal code and county
 */
const getStateProvinceFromCounty = (postalCode, county, state) => {
  if (!postalCode || !county) {
    return undefined
  }

  const countyHash = locations.selectors.getCountyHashSelector(state)
  return countyHash?.[county]?.StateProvince
}

/**
 * Checks if the state/province is eligible for the organ donor question
 */
const isStateEligibleForOrganDonorQuestion = (
  stateProvince,
  eligibleStates
) => {
  if (!stateProvince || !eligibleStates) {
    return false
  }
  return eligibleStates.includes(stateProvince)
}

const mapDispatchToProps = (dispatch) => ({
  onCancel(e) {
    e.preventDefault()
    dispatch(modal.actions.hideModal())
  },
  onAddressOptionSelected: (opt) => {
    dispatch(change(FORM_NAME, 'StreetAddress', `${opt.premise} ${opt.street}`))
    dispatch(change(FORM_NAME, 'StreetAddressExt', opt.suiteOrAptNumber))
    dispatch(change(FORM_NAME, 'City', opt.city))
    dispatch(change(FORM_NAME, 'PostalCode', opt.zipCode.split('-')[0]))
    dispatch(change(FORM_NAME, 'StreetInputHasChanged', true))
    dispatch(change(FORM_NAME, 'County', opt.county))
    dispatch(change(FORM_NAME, 'StateProvince', opt.stateCode))
  },
  onStreetAddressHasChanged: (hasChanged) => {
    dispatch(change(FORM_NAME, 'StreetInputHasChanged', hasChanged))
  },
  onMailAddressOptionSelected: (opt) => {
    const addressLine = opt.street
      ? `${opt.premise} ${opt.street}`
      : opt.addressLine1
    dispatch(change(FORM_NAME, 'MailingStreetAddress', addressLine))
    dispatch(change(FORM_NAME, 'MailingStreetAddressExt', opt.suiteOrAptNumber))
    dispatch(change(FORM_NAME, 'MailingCity', opt.city))
    dispatch(change(FORM_NAME, 'MailingPostalCode', opt.zipCode.split('-')[0]))
    dispatch(change(FORM_NAME, 'MailingCounty', opt.county))
    dispatch(change(FORM_NAME, 'MailingStateProvince', opt.stateCode))
  },
  getBenefitsEffectiveDates: (e) => {
    const hireDate = e.target.value
    dispatch((_, getState) => {
      const formValues = getFormValues(FORM_NAME)(getState()) || {}
      const { AccountCreated, HireDate, EmploymentStatus, PostalCode } =
        formValues
      const body = {
        ...(AccountCreated && { accountCreated: AccountCreated }),
        ...(HireDate && { hireDate: HireDate }),
        ...(hireDate && { hireDate }),
        ...(EmploymentStatus && { employmentStatus: EmploymentStatus }),
      }

      return dispatch(
        BenefitsEffectiveDateActions.getBenefitsEffectiveDates(body)
      ).then((response) => {
        dispatch(
          change(
            FORM_NAME,
            'BenefitsEffectiveDate',
            response.BenefitsEffectiveDate
          )
        )
        dispatch(
          change(FORM_NAME, 'EnrollmentStartDate', response.EnrollmentStartDate)
        )
        dispatch(
          change(FORM_NAME, 'EnrollmentEndDate', response.EnrollmentEndDate)
        )

        // Preserve county selection and reload locations when benefit effective date changes
        if (PostalCode && PostalCode.length === 5) {
          const currentCounty = formValues.County
          // Reset locations for this postal code to force reload
          dispatch(locations.actions.resetLocations(PostalCode))
          // Reload locations with the new benefit effective date year and preserve selection
          dispatch(
            locations.actions.getLocationsWithPreservation(
              PostalCode,
              currentCounty
            )
          )
        }
      })
    })
  },
  onBenefitEffectiveDateChange: (_e) => {
    dispatch((_, getState) => {
      const formValues = getFormValues(FORM_NAME)(getState()) || {}
      const { PostalCode } = formValues

      // Preserve county selection and reload locations when benefit effective date changes
      if (PostalCode && PostalCode.length === 5) {
        const currentCounty = formValues.County
        // Reset locations for this postal code to force reload
        dispatch(locations.actions.resetLocations(PostalCode))
        // Reload locations with the new benefit effective date year and preserve selection
        dispatch(
          locations.actions.getLocationsWithPreservation(
            PostalCode,
            currentCounty
          )
        )
      }
    })
  },
})

const composedEmployeeForm = compose(
  withFeatureFlags,
  PersonProvider((state, props) => ({
    fullProfile: true,
    personId: props.id,
  })),
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    validate: validateEmployeeForm,
    asyncValidate: validateAddress,
    // asyncBlurFields is empty on purpose, because validateAddress need to run AFTER all related fields are updated,
    // otherwise you can get mixed of new data and old data, leading to validation error
    asyncBlurFields: [],
    asyncChangeFields: [
      'PostalCode',
      'County',
      'MailingPostalCode',
      'MailingCounty',
    ], // 'County' field doesnt work with asyncBlurFields for some reason
    warn: checkAddressWarning,
    onSubmit: (values, dispatch, { isSalaryUnavailable, flags, ...props }) => {
      const onSubmitData = async ({
        reason,
        reasonCategory,
        zendeskTicket,
      }) => {
        const { countyHash, action, companyId } = props
        const {
          ExistingMedicalPlanWaived,
          Salary,
          OrganDonorSelection,
          ...newValues
        } = values
        sanitizeValues(newValues)
        const submittedRehireDate =
          newValues.RehireDate == null
            ? ''
            : String(newValues.RehireDate).slice(0, 10)
        delete newValues.RehireDate
        const body = {
          ...newValues,
          Id: props.id,
          StateProvince: countyHash[values.County]?.StateProvince ?? '',
          RatingArea: countyHash[values.County]?.RatingAreaNumeric ?? '',
          MailingStateProvince:
            countyHash[values.MailingCounty]?.StateProvince ?? '',
          Relationship: 'Employee',
          ClientPublicKey: companyId,
          ...(!isSalaryUnavailable && { Salary }),
          ...(OrganDonorSelection && {
            OrganDonorOptInAt:
              OrganDonorSelection === 'yes' ? dayjs().toISOString() : null,
          }),
          ...(action === 'edit' && {
            Id: props.id,
            EmployeePublicKey: props.id,
          }),
        }

        // Log activity after successful submission
        const employeeName = `${body.FirstName} ${body.LastName}`.trim()
        const activityAction =
          action === 'edit'
            ? ActivityLogAction.EDIT_WORKER_INFO
            : ActivityLogAction.ADD_WORKER
        const activityDescription =
          action === 'edit' ? 'edited employee profile' : 'added new employee'

        // Get current user info for activity log
        const state = dispatch((_, getState) => getState())
        const originalPerson = personByIdSelector(state)(props.id)
        const previousRehireDate =
          originalPerson?.RehireDate == null
            ? ''
            : String(originalPerson.RehireDate).slice(0, 10)
        const rehireChanged =
          action === 'edit'
            ? previousRehireDate !== submittedRehireDate
            : submittedRehireDate !== ''
        const clearingStoredDate =
          previousRehireDate !== '' && submittedRehireDate === ''

        const defaultActivityData = {
          action: activityAction,
          description: `${activityDescription} (${employeeName})`,
          targetUserId: props?.id,
        }
        const additionalData = {
          oldData: action === 'edit' ? originalPerson : undefined,
          newData: {
            ...body,
            RehireDate: clearingStoredDate
              ? previousRehireDate
              : submittedRehireDate || null,
          },
          employeeName,
          action: action,
          companyId: companyId,
        }

        return dispatch(
          callWaiter(POST_ROUTE, {
            requestCreator: () =>
              personService({
                route: 'persons',
                method: 'POST',
                body,
              }),
          })
        )
          .then(async (response) => {
            const message =
              action === 'edit'
                ? 'Successfully edited employee profile'
                : 'Successfully inserted a new employee'
            const personId = response?.Id || props.id
            let savedPerson = clearingStoredDate
              ? { ...response, RehireDate: previousRehireDate }
              : response
            let rehireFailed = false
            if (
              rehireChanged &&
              !clearingStoredDate &&
              typeof personId === 'string' &&
              personId !== ''
            ) {
              try {
                await updateRehireDate(personId, submittedRehireDate)
                savedPerson = {
                  ...response,
                  RehireDate: submittedRehireDate,
                }
              } catch (rehireError) {
                rehireFailed = true
              }
            }
            datadog.customAction(
              action === 'edit'
                ? CustomActionNames.EditWorkerInfo
                : CustomActionNames.AddEmployee,
              {
                values: body,
              }
            )
            postActivityLog({
              ...defaultActivityData,
              additionalData: {
                ...additionalData,
                employeeId: response?.Id || props.ids,
                submitStatus: rehireFailed ? 'rehire-date-failed' : 'success',
                reason,
                reasonCategory,
                zendeskTicket,
              },
            })

            dispatch(notification.createSuccessNotification(message))
            if (clearingStoredDate) {
              toast.error('Rehire date cannot be cleared')
            }
            if (rehireFailed) {
              toast.error('Profile saved, but the rehire date was not saved')
            }
            dispatch(personActions.getPersonsByCompanyId(companyId))

            // this dispatch to employee benefits is to keep the benefit data in sync
            // until we get a persons store module rolled out. BT
            dispatch(employeeBenefitsActions.loadPerson(savedPerson))
            dispatch(modal.actions.hideModal())
            if (
              shouldSyncAfterRehireSave(
                originalPerson?.EmploymentStatus,
                body.EmploymentStatus,
                previousRehireDate,
                clearingStoredDate ? previousRehireDate : submittedRehireDate,
                rehireFailed
              )
            ) {
              syncToNoyo({
                employeePublicKey: personId,
                employmentStatus: body.EmploymentStatus,
              }).catch(() => {
                toast.error('Profile saved, but sync to Noyo failed')
              })
            }

            // shows the stop coverage modal when EmployeeStatus is equal to Terminated or Part-Time
            // if (showStopCoverageValues.includes(response.EmploymentStatus)) {
            //   dispatch(setModal(STOP_COVERAGE_MODAL))
            // }
          })
          .catch((error) => {
            // 400
            if (error.status === 400 && isArray(error.errors)) {
              // set errors
              const errors = {}
              error.errors.forEach((err) => {
                errors[err.property] = err.message
              })
              throw new SubmissionError(errors)
            }

            let message =
              action === 'edit'
                ? 'Error editing employee profile'
                : 'Error adding new employee'
            if (error.message) {
              // 406
              if (error.message.status === 406) {
                message +=
                  error.message.data && error.message.data.errors
                    ? error.message.data.errors[0].message
                    : ''
              }
            }

            postActivityLog({
              ...defaultActivityData,
              additionalData: {
                ...additionalData,
                employeeId: props.id,
                submitStatus: 'failed',
                reason,
                reasonCategory,
                zendeskTicket,
              },
            })

            dispatch(notification.createErrorNotification(message))
          })
      }

      if (
        flags['admin.activity-log-reason-popup-enabled'] &&
        !(props.person.isTestUser ?? props.person.IsTestUser)
      ) {
        dispatch(
          openActivityLogPopup({
            contentText: 'Are you sure you want to submit this form?',
            onSubmit: onSubmitData,
            required: true,
          })
        )
      } else {
        onSubmitData({
          reason: '',
          reasonCategory: ActivityReasonCategory.OTHER,
          zendeskTicket: '',
        })
      }
    },
  })
)

export default composedEmployeeForm(EmployeeForm)
